<template>
  <div>
      <div class="mt-7 productdescriptionhtml" :class="{'expanded': showFullDescription}" v-html="productDescription">
      </div>
      <div @click="toggleDescription" class="text-sm flex ml-2 mt-2 cursor-pointer">
          <UiOpen class="close w-2 mr-2" :class="{'open': showFullDescription}" :forceHover="showFullDescription"/>
          {{ showFullDescription ? ' READ LESS' : ' READ MORE' }}
      </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useWindowSize } from '@vueuse/core';
const UiOpen = defineAsyncComponent(() => import('@/components/ui/open.vue'));
const showFullDescription = ref(false);

const { width } = useWindowSize();

// Props
const props = defineProps({
product: {
  type: Object,
  required: true,
}
});

// Toggle description visibility
const toggleDescription = () => {
showFullDescription.value = !showFullDescription.value;
};

// Truncate description based on screen size
const truncatedDescription = computed(() => {
let maxLength = 90; // Default truncation length

// Adjust truncation length based on screen width
if (width.value <= 770) {
  maxLength = 140; // For smaller screens, truncate more aggressively
} else if (width.value <= 1024) {
  maxLength = 85; // For tablets, truncate a little less
}

return props.product.description.length > maxLength
  ? props.product.description.substring(0, maxLength - 3) + "..."
  : props.product.description;
});

// Reactively set description depending on whether it's expanded or truncated
const productDescription = computed(() => {
if (showFullDescription.value) {
  return props.product.descriptionHtml;
} else {
  return '<p>' + truncatedDescription.value + '</p>';
}
});
</script>

<style scoped>
/* Default size for description text */
.productdescriptionhtml {
max-height: 44px; /* Set max-height for truncation */
overflow: hidden;
display: -webkit-box; /* For truncation to work properly */
-webkit-box-orient: vertical; /* Vertical orientation for truncation */
-webkit-line-clamp: 2; /* Clamp to 2 lines */
transition: max-height 0.3s ease-in-out;
}

/* When expanded, allow the full description */
.productdescriptionhtml.expanded {
max-height: 1500px; /* Remove limit when expanded */
-webkit-line-clamp: unset; /* Remove line clamp */
}

/* Responsive adjustments for smaller screens */
@media (max-width: 770px) { /* For screens smaller than 770px */
.productdescriptionhtml {
  max-height: 42px; /* Adjust height for smaller screens */
  -webkit-line-clamp: 2; /* Clamp to 2 lines */
}
}

@media (max-width: 1024px) { /* For screens smaller than 1024px */
.productdescriptionhtml {
  max-height: 42px; /* Adjust height for tablet and smaller devices */
  -webkit-line-clamp: 2; /* Clamp to 2 lines */
}
}

/* Ensure that paragraphs inside description have proper font size */
:deep(.productdescriptionhtml p) {
font-size: 1rem; /* Equivalent to text-base */
}

/* 1) Styla UL: indrag och typ av bullet */
:deep(.productdescriptionhtml ul) {
  padding-left: 1.25rem;      /* justera indraget så att bullets och text får luft */
  margin: 1rem 0;             /* valfritt: avstånd ovan/under listan */
  list-style-type: disc;      /* vanliga ”fyllda” kulor */
  list-style-position: outside; /* kulan sitter utanför textblocket */
  margin-top: 0px;
}

/* 2) Styla LI: textstorlek, radavstånd och mellanrum */
:deep(.productdescriptionhtml ul li) {
  font-size: 1rem;            /* din text-storlek */
  line-height: 1rem;           /* så att bullet hamnar mitt i radens höjd */
  margin-bottom: 0.5rem;      /* lite luft mellan punkterna */
}

/* 3) (Valfritt) Anpassa själva markern om du vill göra den större eller färga den */
:deep(.productdescriptionhtml ul li)::marker {
  font-size: 1.25rem;         /* större bullet än texten */
  color: currentColor;        /* ärva textfärgen */
}
</style>
